import { Grid } from '@mui/material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getCareerPageAnalytics } from 'wisestep-api';
import { WsColors, WsTypography, WsTypoVariants, Item, getDateUsingFormat } from 'wisestep-base';

import { getSumOfColumnRecords } from '../../../composableAnalytics/BaseCharts/clientListing/helper/payloadsAndTypesHelper';
import { IAggregation } from '../../types';
import { collectUniqueDatesFrom1Level } from '../../utils/InternalMISAnalyticsHelper';
import { IMISAnalyticsQuery } from '../IMISAnalyticsQuery';

const CareerPageAnalyticsTable = ({ query, duration }: IMISAnalyticsQuery) => {
  const paginationSize = 10;
  const [aggregatedData, setAggregatedData] = useState<IAggregation[]>([]);
  const [uniqueDates, setUniqueDates] = useState<string[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      const response = await getCareerPageAnalytics(query);
      setAggregatedData(response.data.aggregations.aggregation.buckets);
      setUniqueDates(collectUniqueDatesFrom1Level(response.data.aggregations.aggregation.buckets));
    };
    fetchData();
  }, [query]);

  const dataMap: Record<string, Record<string, number>> = {};

  uniqueDates.forEach((date) => {
    aggregatedData.forEach((item) => {
      const workspace = item.key;
      const bucket = item.aggregation?.buckets?.find((bucket) => bucket.key_as_string === date);

      if (!dataMap[workspace]) {
        dataMap[workspace] = {};
      }

      dataMap[workspace][date + '_views'] = bucket?.viewCount?.value ?? 0;
      dataMap[workspace][date + '_clicks'] = bucket?.applyCount?.value ?? 0;
    });
  });

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'id', hide: true },
    {
      field: 'workspace',
      headerName: query.loadAllWorkspacesData ? 'Workspace' : 'Created By',
      flex: 1,
      minWidth: query.loadAllWorkspacesData ? 210 : 250,
    },
    ...uniqueDates.flatMap((date) => [
      {
        field: date + '_views',
        headerName: getDateUsingFormat(date, 'DD MMM') + '-Views',
        flex: 1,
        minWidth: 200,
      },
      {
        field: date + '_clicks',
        headerName: getDateUsingFormat(date, 'DD MMM') + '-Clicks',
        flex: 1,
        minWidth: 200,
      },
    ]),
  ];

  const tableData: Record<string, unknown>[] = Object.keys(dataMap).map((workspace) => {
    const rowData: Record<string, number | string> = { workspace };
    rowData['id'] = uuidv4();
    uniqueDates.forEach((date) => {
      rowData[date + '_views'] = dataMap[workspace][date + '_views'] ?? 0;
      rowData[date + '_clicks'] = dataMap[workspace][date + '_clicks'] ?? 0;
    });
    return rowData;
  });

  return (
    <Grid item xs={12} bgcolor={WsColors.White}>
      <Item>
        <WsTypography variant={WsTypoVariants.SUB_TITLE} bold={true}>
          {`Career Pages in ${duration ?? 'Last 8 weeks'}`}
        </WsTypography>
      </Item>
      <Item>
        <DataGrid
          rows={tableData}
          columns={getSumOfColumnRecords(tableData, columns, ['id', 'workspace'])}
          components={{ Toolbar: GridToolbar }}
          pageSize={paginationSize}
          rowsPerPageOptions={[paginationSize]}
          autoHeight={true}
        />
      </Item>
    </Grid>
  );
};
export default CareerPageAnalyticsTable;
